import { useState } from "react";
import { Download, Instagram, Facebook, Linkedin, Share2, Check, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const profileImage = "https://customer-assets.emergentagent.com/job_1e4866aa-1b76-485b-9ba8-54acbae14c92/artifacts/0lvrpq7z_IMG_5316.jpeg";
const rafhaLogo = "https://customer-assets.emergentagent.com/job_choice-omw-launch/artifacts/66p7nc88_Rapha%20Entertainment%20Logo.png";
const choiceLogo = "https://customer-assets.emergentagent.com/job_choice-omw-launch/artifacts/zcquxkzd_IMG_0221.png";

// Instagram Square Flyer (1:1)
const InstagramSquareFlyer = () => (
  <div 
    id="instagram-square"
    className="relative w-[540px] h-[540px] bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 overflow-hidden"
    style={{ fontFamily: "'Playfair Display', serif" }}
  >
    {/* Warm overlay pattern */}
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-amber-300/40 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-rose-300/30 to-transparent rounded-full blur-3xl" />
    </div>

    {/* Top decorative bar */}
    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

    {/* Content container */}
    <div className="relative z-10 h-full flex flex-col p-6">
      {/* Header */}
      <div className="text-center mb-3">
        <p className="text-amber-700 text-xs uppercase tracking-[0.3em] font-medium mb-1">Exciting News</p>
        <h1 className="text-2xl font-bold text-stone-800 leading-tight">
          New Ventures,<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-rose-600">Same Passion</span>
        </h1>
      </div>

      {/* Photo with artistic treatment */}
      <div className="flex justify-center items-center mb-3">
        <div className="relative">
          {/* Decorative rings */}
          <div className="absolute -inset-2 rounded-full border-2 border-amber-300/50" />
          
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/30 to-rose-400/30 blur-xl scale-110" />
          
          {/* Photo */}
          <div className="relative w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-xl">
            <img 
              src={profileImage} 
              alt="Entrepreneur" 
              className="w-full h-full object-cover object-top"
              style={{ filter: 'brightness(1.05) contrast(1.02) saturate(1.1)' }}
            />
            {/* Warm overlay on photo */}
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/20 via-transparent to-rose-100/10" />
          </div>

          {/* Sparkle decorations */}
          <div className="absolute -top-1 -right-1 text-amber-500 text-sm">✦</div>
        </div>
      </div>

      {/* Businesses */}
      <div className="space-y-1.5 mb-3">
        <div className="text-center space-y-0.5">
          <p className="text-stone-800 font-semibold text-sm">🎵 Rafha Entertainment</p>
          <p className="text-stone-800 font-semibold text-sm">🎉 Choice Mobile Celebrations</p>
          <p className="text-stone-700 text-xs">+ Reintroducing <span className="font-semibold">💼 OMW Business & Financial</span></p>
        </div>
      </div>

      {/* Ebook Highlight */}
      <div className="bg-gradient-to-r from-stone-800 to-stone-900 rounded-xl p-3 mb-3 text-center">
        <p className="text-amber-400 text-[10px] uppercase tracking-wider mb-1">📚 New Ebook</p>
        <p className="text-white font-bold text-sm leading-tight">
          "Stop Living Paycheck<br />to Paycheck"
        </p>
        <p className="text-amber-300/80 text-xs mt-1">Real Solutions to Build Wealth</p>
      </div>

      {/* CTA */}
      <div className="text-center mt-auto">
        <div className="inline-block bg-gradient-to-r from-amber-500 to-rose-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
          Follow for Updates ✨
        </div>
      </div>
    </div>
  </div>
);

// Instagram Story Flyer (9:16)
const InstagramStoryFlyer = () => (
  <div 
    id="instagram-story"
    className="relative w-[405px] h-[720px] bg-gradient-to-b from-stone-800 via-stone-900 to-stone-950 overflow-hidden"
    style={{ fontFamily: "'Playfair Display', serif" }}
  >
    {/* Warm color accents */}
    <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-amber-500/20 to-transparent" />
    <div className="absolute bottom-0 left-0 w-full h-1/4 bg-gradient-to-t from-rose-500/15 to-transparent" />
    
    {/* Decorative elements */}
    <div className="absolute top-16 right-8 text-amber-400/60 text-3xl">✦</div>
    <div className="absolute top-32 left-6 text-rose-400/40 text-xl">✦</div>

    <div className="relative z-10 h-full flex flex-col p-6">
      {/* Header */}
      <div className="text-center pt-6 mb-4">
        <p className="text-amber-400 text-xs uppercase tracking-[0.4em] mb-2">✨ Announcement ✨</p>
        <h1 className="text-3xl font-bold text-white leading-tight">
          Something<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-rose-400">
            Exciting
          </span><br />
          Is Here!
        </h1>
      </div>

      {/* Photo */}
      <div className="flex justify-center items-center mb-4">
        <div className="relative">
          {/* Glow */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-500/40 to-rose-500/40 blur-2xl scale-125" />
          
          {/* Decorative ring */}
          <div className="absolute -inset-3 rounded-full border-2 border-amber-400/30" />
          
          {/* Photo */}
          <div className="relative w-44 h-44 rounded-full overflow-hidden border-4 border-amber-400/50 shadow-2xl">
            <img 
              src={profileImage} 
              alt="Entrepreneur" 
              className="w-full h-full object-cover object-top"
              style={{ filter: 'brightness(1.1) contrast(1.05) saturate(1.15)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/30 via-transparent to-rose-400/10" />
          </div>
        </div>
      </div>

      {/* Businesses */}
      <div className="space-y-2 mb-4">
        <p className="text-amber-300/80 text-xs text-center uppercase tracking-widest">Now Launching</p>
        
        <div className="space-y-1.5 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg py-1.5 px-4 mx-6">
            <p className="text-white font-semibold text-sm">🎵 Rafha Entertainment</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg py-1.5 px-4 mx-6">
            <p className="text-white font-semibold text-sm">🎉 Choice Mobile Celebrations</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-lg py-1.5 px-4 mx-10">
            <p className="text-amber-200 text-xs">+ Reintroducing</p>
            <p className="text-white font-semibold text-sm">💼 OMW Business & Financial</p>
          </div>
        </div>
      </div>

      {/* Ebook Highlight - Featured */}
      <div className="bg-gradient-to-r from-amber-600/90 to-rose-600/90 rounded-2xl p-4 mx-4 mb-4 text-center border border-amber-400/30">
        <p className="text-amber-100 text-[10px] uppercase tracking-widest mb-1">📚 NEW EBOOK LAUNCH</p>
        <p className="text-white font-bold text-lg leading-tight mb-1">
          "Stop Living Paycheck<br />to Paycheck"
        </p>
        <p className="text-amber-100 text-sm">Real Solutions to Build Wealth</p>
        <p className="text-white/70 text-xs mt-2">By Lavista Smith</p>
      </div>

      {/* CTA */}
      <div className="text-center mt-auto pb-6">
        <div className="inline-block bg-gradient-to-r from-amber-500 to-rose-500 text-white px-8 py-3 rounded-full font-bold shadow-lg shadow-amber-500/30">
          Follow for Updates
        </div>
        <p className="text-amber-300/60 text-xs mt-3">Swipe Up to Learn More ↑</p>
      </div>
    </div>
  </div>
);

// Facebook/LinkedIn Landscape Flyer (1.91:1)
const LandscapeFlyer = ({ platform }) => (
  <div 
    id={platform}
    className="relative w-[600px] h-[314px] bg-gradient-to-r from-stone-50 via-amber-50 to-rose-50 overflow-hidden"
    style={{ fontFamily: "'Playfair Display', serif" }}
  >
    {/* Decorative background */}
    <div className="absolute inset-0">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-100/50 to-transparent" />
      <div className="absolute bottom-0 left-1/3 w-96 h-32 bg-gradient-to-t from-rose-100/40 to-transparent blur-2xl" />
    </div>

    {/* Accent bar */}
    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

    <div className="relative z-10 h-full flex">
      {/* Left - Photo */}
      <div className="w-1/3 h-full flex items-center justify-center p-4">
        <div className="relative">
          {/* Glow */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/30 to-rose-400/30 blur-xl scale-110" />
          
          {/* Rings */}
          <div className="absolute -inset-2 rounded-full border-2 border-amber-300/40" />
          
          {/* Photo */}
          <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl">
            <img 
              src={profileImage} 
              alt="Entrepreneur" 
              className="w-full h-full object-cover object-top"
              style={{ filter: 'brightness(1.05) contrast(1.02) saturate(1.1)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/20 via-transparent to-transparent" />
          </div>
          
          {/* Sparkle */}
          <div className="absolute -top-1 -right-1 text-amber-500 text-sm">✦</div>
        </div>
      </div>

      {/* Right - Content */}
      <div className="w-2/3 h-full flex flex-col justify-center pr-6 py-4">
        <p className="text-amber-600 text-[10px] uppercase tracking-[0.25em] mb-1">✨ Exciting Announcement</p>
        
        <h1 className="text-xl font-bold text-stone-800 leading-tight mb-2">
          New Ventures,{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-rose-600">
            Same Heart
          </span>
        </h1>

        <div className="flex gap-4 mb-3">
          {/* Businesses - Compact */}
          <div className="space-y-0.5 text-xs">
            <p className="text-stone-700 font-medium">🎵 Rafha Entertainment</p>
            <p className="text-stone-700 font-medium">🎉 Choice Mobile Celebrations</p>
            <p className="text-stone-600">+ 💼 OMW Business & Financial</p>
          </div>

          {/* Ebook Highlight */}
          <div className="bg-stone-800 rounded-lg p-2 text-center flex-1">
            <p className="text-amber-400 text-[8px] uppercase tracking-wider">📚 New Ebook</p>
            <p className="text-white font-bold text-xs leading-tight">
              "Stop Living Paycheck to Paycheck"
            </p>
            <p className="text-amber-300/80 text-[9px]">Build Wealth with Purpose</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-amber-500 to-rose-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md">
            Follow for Updates
          </div>
          <span className="text-stone-500 text-[10px]">Let's grow together! 🌟</span>
        </div>
      </div>
    </div>
  </div>
);

// Facebook/Instagram Story Vendor Recruitment Flyer (9:16)
const VendorRecruitmentStoryFlyer = () => (
  <div 
    id="vendor-recruitment-story"
    className="relative w-[405px] h-[720px] bg-gradient-to-b from-stone-900 via-stone-800 to-stone-950 overflow-hidden"
    style={{ fontFamily: "'Playfair Display', serif" }}
  >
    {/* Top accent bar */}
    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
    
    {/* Decorative elements */}
    <div className="absolute top-20 right-6 text-amber-400/50 text-2xl">✦</div>
    <div className="absolute top-40 left-8 text-rose-400/30 text-lg">✦</div>
    <div className="absolute bottom-40 right-10 text-amber-400/40 text-xl">✦</div>
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
    <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl" />

    <div className="relative z-10 h-full flex flex-col p-6">
      {/* Header */}
      <div className="text-center pt-6 mb-6">
        <p className="text-amber-400 text-xs uppercase tracking-[0.3em] mb-2">📣 Now Launching</p>
        <h1 className="text-3xl font-bold text-white leading-tight">
          Join Our
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-rose-400">
            Elite Vendor
          </span>
          Network
        </h1>
      </div>

      {/* Logos */}
      <div className="flex justify-center gap-4 mb-6">
        <div className="bg-white rounded-xl p-2 shadow-lg w-28 h-28 flex items-center justify-center">
          <img src={rafhaLogo} alt="Rafha Entertainment" className="w-full h-full object-contain" />
        </div>
        <div className="bg-white rounded-xl p-2 shadow-lg w-28 h-28 flex items-center justify-center">
          <img src={choiceLogo} alt="Choice Mobile Celebrations" className="w-full h-full object-contain" />
        </div>
      </div>

      {/* Description */}
      <div className="text-center mb-6">
        <p className="text-stone-300 text-sm leading-relaxed px-4">
          We're building a premium marketplace of vetted entertainment & celebration professionals. 
          Grow your business with us!
        </p>
      </div>

      {/* Vendor types */}
      <div className="flex-1">
        <p className="text-amber-400 text-xs text-center uppercase tracking-wider mb-3">We're Looking For</p>
        <div className="flex flex-wrap justify-center gap-2 px-2">
          {["DJs", "Photographers", "Caterers", "Musicians", "Decorators", "MCs", "Security", "Bakers", "Comedians", "Event Planners", "Balloon Artists"].map((type) => (
            <span key={type} className="bg-amber-500/20 text-amber-300 text-xs px-3 py-1.5 rounded-full border border-amber-500/30">
              {type}
            </span>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 mb-6 border border-amber-500/20">
        <p className="text-amber-400 text-xs uppercase tracking-wider mb-2 text-center">Why Join?</p>
        <div className="space-y-1.5 text-sm text-stone-300">
          <p className="flex items-center gap-2"><span className="text-amber-400">✓</span> Vetted & trusted platform</p>
          <p className="flex items-center gap-2"><span className="text-amber-400">✓</span> Expand your client base</p>
          <p className="flex items-center gap-2"><span className="text-amber-400">✓</span> Professional support</p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center pb-6">
        <div className="inline-block bg-gradient-to-r from-amber-500 to-rose-500 text-white px-8 py-3 rounded-full font-bold shadow-lg shadow-amber-500/30">
          Apply Now
        </div>
        <p className="text-amber-300/70 text-xs mt-3">rafhaentertainment.com</p>
        <p className="text-stone-500 text-xs mt-2">Swipe Up to Apply ↑</p>
      </div>
    </div>
  </div>
);

// Facebook Vendor Recruitment Flyer
const VendorRecruitmentFlyer = () => (
  <div 
    id="vendor-recruitment"
    className="relative w-[600px] h-[314px] bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 overflow-hidden"
    style={{ fontFamily: "'Playfair Display', serif" }}
  >
    {/* Decorative elements */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
    <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl" />
    
    {/* Sparkles */}
    <div className="absolute top-8 right-12 text-amber-400/60 text-xl">✦</div>
    <div className="absolute bottom-12 left-16 text-amber-400/40 text-sm">✦</div>

    <div className="relative z-10 h-full flex">
      {/* Left - Logos */}
      <div className="w-2/5 h-full flex flex-col items-center justify-center p-6 border-r border-amber-500/20">
        <p className="text-amber-400 text-[10px] uppercase tracking-widest mb-4">Now Launching</p>
        
        <div className="flex flex-col items-center gap-4">
          {/* Rafha Logo */}
          <div className="bg-white rounded-xl p-2 shadow-lg w-24 h-24 flex items-center justify-center">
            <img src={rafhaLogo} alt="Rafha Entertainment" className="w-full h-full object-contain" />
          </div>
          
          <div className="text-amber-400 text-lg font-bold">+</div>
          
          {/* Choice Logo */}
          <div className="bg-white rounded-xl p-2 shadow-lg w-24 h-24 flex items-center justify-center">
            <img src={choiceLogo} alt="Choice Mobile Celebrations" className="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      {/* Right - Content */}
      <div className="w-3/5 h-full flex flex-col justify-center px-6 py-4">
        <div className="mb-3">
          <p className="text-amber-400 text-[10px] uppercase tracking-widest mb-1">📣 Calling All Professionals</p>
          <h1 className="text-2xl font-bold text-white leading-tight">
            Join Our
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-rose-400"> Elite Vendor </span>
            Network
          </h1>
        </div>

        <p className="text-stone-300 text-xs leading-relaxed mb-4">
          We're building a premium marketplace of vetted entertainment & celebration professionals. 
          DJs, photographers, caterers, musicians & more — grow your business with us!
        </p>

        {/* Vendor types */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {["DJs", "Photographers", "Caterers", "Musicians", "Decorators", "MCs", "Security", "Bakers", "Comedians", "Event Planners", "Balloon Artists"].map((type) => (
            <span key={type} className="bg-amber-500/20 text-amber-300 text-[10px] px-2 py-1 rounded-full border border-amber-500/30">
              {type}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-amber-500 to-rose-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
            Apply Now → rafhaentertainment.com
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Main component
const SocialMediaFlyers = () => {
  const [activeTab, setActiveTab] = useState("instagram-square");
  const [copied, setCopied] = useState(false);

  const platforms = [
    { id: "instagram-square", label: "Instagram Post", icon: Instagram, size: "1080 × 1080" },
    { id: "instagram-story", label: "Instagram Story", icon: Instagram, size: "1080 × 1920" },
    { id: "facebook", label: "Facebook", icon: Facebook, size: "1200 × 628" },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, size: "1200 × 627" },
    { id: "vendor-recruitment", label: "Vendor (Post)", icon: Users, size: "1200 × 628" },
    { id: "vendor-story", label: "Vendor (Story)", icon: Users, size: "1080 × 1920" },
  ];

  const handleDownloadTip = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 via-amber-50/30 to-rose-50/30 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Social Media Ready
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Announcement Flyers
          </h1>
          <p className="text-stone-600 max-w-xl mx-auto">
            Warm, personal designs featuring your new ventures and ebook launch. 
            Screenshot or right-click to save each design!
          </p>
        </div>

        {/* Download Instructions */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-amber-200/50 shadow-sm max-w-2xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Download className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-800 mb-1">How to Save Your Flyers</h3>
              <p className="text-stone-600 text-sm mb-3">
                <strong>Option 1:</strong> Right-click on the flyer → "Save image as..."<br />
                <strong>Option 2:</strong> Take a screenshot of the flyer design<br />
                <strong>Option 3:</strong> Use your browser's print function to save as PDF
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadTip}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> Got it!</> : <><Share2 className="w-4 h-4 mr-2" /> Tips for Best Quality</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Platform Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-2 bg-white/50 p-2 rounded-xl mb-8 max-w-5xl mx-auto h-auto">
            {platforms.map((platform) => (
              <TabsTrigger 
                key={platform.id}
                value={platform.id}
                className="flex flex-col items-center gap-1 py-3 px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-rose-500 data-[state=active]:text-white rounded-lg"
              >
                <platform.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{platform.label}</span>
                <span className="text-[10px] opacity-70">{platform.size}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Flyer Previews */}
          <div className="flex justify-center">
            <TabsContent value="instagram-square" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <InstagramSquareFlyer />
              </div>
            </TabsContent>

            <TabsContent value="instagram-story" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <InstagramStoryFlyer />
              </div>
            </TabsContent>

            <TabsContent value="facebook" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <LandscapeFlyer platform="facebook" />
              </div>
            </TabsContent>

            <TabsContent value="linkedin" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <LandscapeFlyer platform="linkedin" />
              </div>
            </TabsContent>

            <TabsContent value="vendor-recruitment" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <VendorRecruitmentFlyer />
              </div>
            </TabsContent>

            <TabsContent value="vendor-story" className="mt-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl inline-block">
                <VendorRecruitmentStoryFlyer />
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Quick Tips */}
        <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/70 backdrop-blur-sm rounded-xl p-5 border border-amber-100">
            <Instagram className="w-8 h-8 text-rose-500 mb-3" />
            <h4 className="font-semibold text-stone-800 mb-1">Instagram Tips</h4>
            <p className="text-stone-600 text-sm">Post the square version to your feed, use the story version for Stories. Add relevant hashtags!</p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-xl p-5 border border-amber-100">
            <Facebook className="w-8 h-8 text-blue-600 mb-3" />
            <h4 className="font-semibold text-stone-800 mb-1">Facebook Tips</h4>
            <p className="text-stone-600 text-sm">Share to your profile, business page, and relevant groups. Pin to the top of your page!</p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-xl p-5 border border-amber-100">
            <Linkedin className="w-8 h-8 text-blue-700 mb-3" />
            <h4 className="font-semibold text-stone-800 mb-1">LinkedIn Tips</h4>
            <p className="text-stone-600 text-sm">Perfect for professional networking. Add a personal story about your entrepreneurial journey!</p>
          </div>
        </div>

        {/* Back to full site link */}
        <div className="text-center mt-10">
          <a 
            href="/"
            className="text-amber-600 hover:text-amber-700 font-medium underline underline-offset-4"
          >
            ← Back to Full Announcement Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaFlyers;
